import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { TStore, useAppDispatch } from '../store';
import { getRecoveryData } from '../actions/recoveryAction';
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar } from 'recharts';
import { RecoveryAnalyzer, RecoveryComponent, RecoveryMetrics } from '../utils/recoveryAnalyzer';

const Recovery: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { recoveryData, loading, error } = useSelector((state: TStore) => state.recovery);
  const selectedVessel = useSelector((state: TStore) => state.averageFleetDetails.selectedVessel);
  
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [viewMode, setViewMode] = useState<'overview' | 'components' | 'trends'>('overview');

  // Fetch recovery data on component mount and when vessel changes
  useEffect(() => {
    if (selectedVessel?.shipId) {
      dispatch(getRecoveryData(selectedVessel.shipId));
    } else {
      dispatch(getRecoveryData(1));
    }
  }, [dispatch, selectedVessel?.shipId]);

  // STEP 1: Filter data by selected vessel first (if applicable)
  const vesselFilteredData = React.useMemo(() => {
    if (!recoveryData || recoveryData.length === 0) return [];
    
    let vesselData = recoveryData;
    if (selectedVessel?.shipId) {
      vesselData = recoveryData.filter(item => item.shipId === selectedVessel.shipId);
    }
    
    return vesselData;
  }, [recoveryData, selectedVessel?.shipId]);

  // STEP 2: Aggregate data by date (multiple users per date become one record per date)
  const aggregatedData = React.useMemo(() => {
    const aggregated = RecoveryAnalyzer.aggregateDataByDate(vesselFilteredData);
    console.log('Aggregated recovery data length:', aggregated.length);
    console.log('Sample aggregated recovery data:', aggregated.slice(0, 3));
    return aggregated;
  }, [vesselFilteredData]);

  // STEP 3: Filter by time range (7d, 30d, 90d)
  const filteredData = React.useMemo(() => {
    const timeFiltered = RecoveryAnalyzer.filterDataByTimeRange(aggregatedData, timeRange);
    console.log(`Time filtered recovery data (${timeRange}):`, timeFiltered.length);
    return timeFiltered;
  }, [aggregatedData, timeRange]);

  // Calculate metrics from real data - adjusted to handle available data
  const metrics: RecoveryMetrics = React.useMemo(() => {
    if (filteredData.length === 0) {
      return {
        current: 0,
        trend: 'stable',
        weeklyAverage: 0,
        monthlyAverage: 0,
        greenDays: 0,
        yellowDays: 0,
        redDays: 0
      };
    }

    const weeklyData = filteredData.slice(-7);
    const validRecoveryData = filteredData.filter(d => d.recovery > 0).map(d => d.recovery);
    
    return {
      current: selectedVessel?.averageRecovery ?? filteredData[filteredData.length - 1]?.recovery ?? 0,
      trend: RecoveryAnalyzer.calculateTrend(validRecoveryData, timeRange),
      weeklyAverage: weeklyData.reduce((a, b) => a + b.recovery, 0) / weeklyData.length,
      monthlyAverage: validRecoveryData.reduce((a, b) => a + b, 0) / validRecoveryData.length,
      greenDays: filteredData.filter(d => d.recovery >= 67).length,
      yellowDays: filteredData.filter(d => d.recovery >= 34 && d.recovery < 67).length,
      redDays: filteredData.filter(d => d.recovery < 34).length,
    };
  }, [filteredData, selectedVessel?.averageRecovery]);

  const currentZone = RecoveryAnalyzer.determineZone(metrics.current);
  const latestData = filteredData[filteredData.length - 1];

  const recoveryComponents: RecoveryComponent[] = React.useMemo(() => {
    if (!latestData) return [];
    
    const components = [];
    
    // Only show components that have data
    if (latestData.heartRateVariability > 0) {
      components.push({
        name: 'HRV',
        value: latestData.heartRateVariability,
        baseline: 45,
        unit: 'ms',
        impact: 65,
        color: '#4ECDC4'
      });
    }
    
    if (latestData.restingHeartRate > 0) {
      components.push({
        name: 'Resting HR',
        value: latestData.restingHeartRate,
        baseline: 65,
        unit: 'bpm',
        impact: 25,
        color: '#FF6B6B'
      });
    }
    
    if (latestData.sleepScore > 0) {
      components.push({
        name: 'Sleep Score',
        value: latestData.sleepScore,
        baseline: 85,
        unit: '%',
        impact: 10,
        color: '#4DABF7'
      });
    }
    
    return components;
  }, [latestData]);

  // Prepare chart data
  const chartData = filteredData.map(item => ({
    ...item,
    heartRateVariability: item.heartRateVariability.toFixed(2),
    recovery: item.recovery.toFixed(2),
    restingHeartRate: item.restingHeartRate.toFixed(2),
    date: new Date(item.date).toISOString().split('T')[0]
  }));


  if (loading) {
    return (
      <div className="recovery-analysis">
        <Spin className='recovery-analysis__loading' />
      </div>
    );
  }

  if (error) {
    return (
      <div className="recovery-analysis">
        <div className="recovery-analysis__error">Error loading recovery data: {error}</div>
      </div>
    );
  }

  if (filteredData.length === 0) {
    return (
      <div className="recovery-analysis">
        <div className="recovery-analysis__no-data">
          No recovery data available for the selected time range and vessel.
        </div>
      </div>
    );
  }

  return (
    <div className="recovery-analysis">
      {/* Header */}
      <div className="recovery-analysis__header">
        <div className="recovery-analysis__title-section">
          <h1 className="recovery-analysis__title">Recovery Analysis</h1>
        </div>
        
        <div className="recovery-analysis__controls">
          <div className="control-group" role="group" aria-label="Time range">
            <button
              className={`control-btn ${timeRange === '7d' ? 'control-btn--active' : ''}`}
              onClick={() => setTimeRange('7d')}
              aria-pressed={timeRange === '7d'}
            >
              7D
            </button>
            <button
              className={`control-btn ${timeRange === '30d' ? 'control-btn--active' : ''}`}
              onClick={() => setTimeRange('30d')}
              aria-pressed={timeRange === '30d'}
            >
              30D
            </button>
            <button
              className={`control-btn ${timeRange === '90d' ? 'control-btn--active' : ''}`}
              onClick={() => setTimeRange('90d')}
              aria-pressed={timeRange === '90d'}
            >
              90D
            </button>
          </div>

          <div className="control-group" role="group" aria-label="View mode">
            <button
              className={`control-btn ${viewMode === 'overview' ? 'control-btn--active' : ''}`}
              onClick={() => setViewMode('overview')}
              aria-pressed={viewMode === 'overview'}
            >
              Overview
            </button>
            {recoveryComponents.length > 0 && (
              <button
                className={`control-btn ${viewMode === 'components' ? 'control-btn--active' : ''}`}
                onClick={() => setViewMode('components')}
                aria-pressed={viewMode === 'components'}
              >
                Components
              </button>
            )}
            <button
              className={`control-btn ${viewMode === 'trends' ? 'control-btn--active' : ''}`}
              onClick={() => setViewMode('trends')}
              aria-pressed={viewMode === 'trends'}
            >
              Trends
            </button>
          </div>
          <button className="btn-back" onClick={() => navigate(-1)}>
            <span className="btn-back__arrow" aria-hidden="true">←</span>
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Current Recovery Display */}
      <div className="recovery-analysis__current">
        <div className="current-recovery">
          <div className="recovery-circle">
            <ResponsiveContainer width={150} height={150}>
              <RadialBarChart 
                cx="50%" 
                cy="50%" 
                innerRadius="70%" 
                outerRadius="90%" 
                data={[{ value: metrics.current }]}
                startAngle={90}
                endAngle={450}
              >
                <RadialBar 
                  dataKey="value" 
                  cornerRadius={10} 
                  fill={currentZone.color}
                />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="recovery-score">
              <span className="recovery-score__value">{Math.round(metrics.current)}%</span>
              <span className="recovery-score__label">Recovery</span>
            </div>
          </div>
          
          <div className="recovery-status">
            <div className="recovery-status__indicator" style={{ backgroundColor: currentZone.color }}>
              {currentZone.label}
            </div>
            <div className="recovery-status__description">
              {currentZone.description}
            </div>
          </div>
        </div>
        
        <div className="recovery-metrics">
          <div className="metric-card">
            <div className="metric-card__value">{Math.round(metrics.monthlyAverage)}%</div>
            <div className="metric-card__label">Period Avg</div>
          </div>
          <div className="metric-card">
            <div className="metric-card__value">{metrics.greenDays}</div>
            <div className="metric-card__label">Green Days</div>
          </div>
          <div className="metric-card">
            <div className="metric-card__value">
              <span className={`trend-indicator trend-indicator--${metrics.trend}`}>
                {metrics.trend === 'improving' ? '↗' : metrics.trend === 'declining' ? '↘' : '→'}
              </span>
            </div>
            <div className="metric-card__label">Trend</div>
          </div>
        </div>
      </div>

      <div className="recovery-analysis__content">
        {viewMode === 'overview' && (
          <div className="overview-container">
            <div className="chart-section">
              <h3 className="chart-section__title">Recovery Over Time</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Recovery %', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value) => [
                      typeof value === 'string' ? "Recovery " + value + " %" : ""
                    ]}
                    labelFormatter={(label) => new Date(label).toLocaleDateString()}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="recovery" 
                    stroke="#00C851" 
                    strokeWidth={3}
                    dot={{ fill: '#00C851', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#00C851', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="zone-distribution">
              <h3 className="zone-distribution__title">Recovery Zone Distribution</h3>
              <div className="zone-distribution__bars">
                {RecoveryAnalyzer.getRecoveryZones().map((zone, index) => {
                  const count = filteredData.filter(d => d.recovery >= zone.min && d.recovery <= zone.max).length;
                  const percentage = filteredData.length > 0 ? (count / filteredData.length) * 100 : 0;
                  return (
                    <div key={index} className="zone-distribution__bar">
                      <div className="zone-distribution__info">
                        <span className="zone-distribution__name" style={{ color: zone.color }}>
                          {zone.label}
                        </span>
                        <span className="zone-distribution__range">
                          {zone.min}-{zone.max}%
                        </span>
                      </div>
                      <div className="zone-distribution__progress">
                        <div 
                          className="zone-distribution__fill" 
                          style={{ 
                            width: `${percentage}%`, 
                            backgroundColor: zone.color 
                          }}
                        ></div>
                      </div>
                      <div className="zone-distribution__stats">
                        <span className="zone-distribution__count">{count} days</span>
                        <span className="zone-distribution__percentage">{percentage.toFixed(0)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {viewMode === 'components' && recoveryComponents.length > 0 && (
          <div className="components-container">
            <h3 className="components-container__title">Recovery Components</h3>
            <div className="components-grid">
              {recoveryComponents.map((component, index) => (
                <div key={index} className="component-card">
                  <div className="component-card__header">
                    <div className="component-card__name">{component.name}</div>
                    <div className="component-card__impact">
                      {component.impact}% impact
                    </div>
                  </div>
                  <div className="component-card__value" style={{ color: component.color }}>
                    {component.value.toFixed(1)} {component.unit}
                  </div>
                  <div className="component-card__baseline">
                    Baseline: {component.baseline} {component.unit}
                  </div>
                  <div className="component-card__progress">
                    <div 
                      className="component-card__fill" 
                      style={{ 
                        width: `${Math.min(100, (component.value / component.baseline) * 100)}%`,
                        backgroundColor: component.color 
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {viewMode === 'trends' && (
          <div className="trends-container">
            <h3 className="trends-container__title">Recovery Trends</h3>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  labelFormatter={(label) => new Date(label).toLocaleDateString()}
                />
                <Line 
                  type="monotone" 
                  dataKey="recovery" 
                  stroke="#00C851" 
                  strokeWidth={2}
                  name="Recovery Score"
                />
                {latestData?.heartRateVariability > 0 && (
                  <Line 
                    type="monotone" 
                    dataKey="heartRateVariability" 
                    stroke="#4ECDC4" 
                    strokeWidth={2}
                    name="HRV"
                  />
                )}
                {latestData?.restingHeartRate > 0 && (
                  <Line 
                    type="monotone" 
                    dataKey="restingHeartRate" 
                    stroke="#FF6B6B" 
                    strokeWidth={2}
                    name="Resting HR"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default Recovery;